import { adaptRestStatus } from 'app/components/integrations-locations/statusModel'
import type { RawIntegrationStatus, ResolvedStatus } from 'app/components/integrations-locations/statusModel'
import type { IntegrationsRow } from 'app/types/integrations'
import { gqlQuery } from 'app/lib/graphqlClient'
import { DNI_INTEGRATION_STATUS_QUERY } from 'app/components/dni/graphql'
import { LOCATION_INTEGRATION_STATUS_QUERY } from 'app/components/integrations-locations/graphql'
import type { DniIntegrationStatusQuery } from 'app/generated/gql/graphql'

export type { RawIntegrationStatus } from 'app/components/integrations-locations/statusModel'

// Map the human-facing integration label to the API endpoint slug. Kept here
// (rather than duplicated at each call site) so LazyIntegrationStatus and the
// grouped Locations view resolve status through the exact same slug + cache.
const INTEGRATION_ENDPOINT: Record<string, string> = {
  'Facebook Ads': `facebook-ads`,
  'Facebook Leads': `facebook-leads`,
}

export function integrationEndpointSlug(integration: string): string {
  return INTEGRATION_ENDPOINT[integration] || integration.toLowerCase()
}

// Module-level promise cache keyed by `${locationId}${slug}`, shared across
// every consumer so the status for a given location+integration is fetched at
// most once per session. Failed lookups are evicted so a later mount retries.
const statusPromises: Record<string, Promise<RawIntegrationStatus | undefined>> = {}

export function fetchIntegrationStatus(
  integrationSlug: string,
  locationId: string,
): Promise<RawIntegrationStatus | undefined> {
  const cacheKey = `${locationId}${integrationSlug}`
  if (!statusPromises[cacheKey]) {
    const promise = window
      .fetch(`/api/integrations/${integrationSlug}/${locationId}/status`, { credentials: `include` })
      .then(async (r): Promise<RawIntegrationStatus | undefined> => {
        if (r.ok) return (await r.json()) as RawIntegrationStatus
        return r.status === 400 ? { unsupported: true } : undefined
      })
      .catch(() => undefined)
    statusPromises[cacheKey] = promise
    // `invalidateIntegrationStatuses` can swap (or clear) `cacheKey` while this
    // request is still in flight. Only evict the entry if it's still the same
    // promise this handler was attached to -- otherwise a slow failing request
    // could delete a newer, unrelated in-flight (or already-cached) entry.
    promise
      .then((result) => {
        if (!result && statusPromises[cacheKey] === promise) delete statusPromises[cacheKey]
      })
      .catch(() => {})
  }
  return statusPromises[cacheKey]
}

/**
 * Resolve the adapted status for an integration slug. Google and CallRail go
 * through the batched GraphQL contract (the REST route has no Google case and
 * CallRail staleness keys off a sync-freshness signal the resolver computes,
 * TRK-6115); everything else still reads the per-integration REST payload.
 */
export function fetchSlugResolvedStatus(slug: string, locationId: string): Promise<ResolvedStatus | undefined> {
  if (slug === `google` || slug === `callrail`) {
    const gqlStatus = gqlQuery(
      LOCATION_INTEGRATION_STATUS_QUERY,
      { integration: slug, locationId },
      { requestPolicy: `network-only` },
    )
    return gqlStatus.then((result) => {
      if (!result.data) return undefined
      const { status, ...data } = result.data.integrationStatus
      return adaptRestStatus(slug, { status, data })
    })
  }
  return fetchIntegrationStatus(slug, locationId).then((raw) => adaptRestStatus(slug, raw))
}

/** Resolve the adapted status for a locations-list row via its integration slug. */
export function fetchRowResolvedStatus(row: IntegrationsRow): Promise<ResolvedStatus | undefined> {
  const slug = integrationEndpointSlug(row.integration)
  // DNI is a Trackable-owned model, not a third-party integration, so its status
  // comes from GraphQL rather than the deprecated REST status endpoint.
  if (row.integration === `Dynamic Number Insertion`) {
    const dniStatus = gqlQuery<DniIntegrationStatusQuery>(DNI_INTEGRATION_STATUS_QUERY, {
      locationId: row.locationId,
    })
    return dniStatus.then((result) => adaptRestStatus(slug, { status: result.data?.integrationStatus.status ?? `` }))
  }
  return fetchSlugResolvedStatus(slug, row.locationId)
}

/**
 * Clear every cached status lookup so the next `fetchIntegrationStatus`/
 * `fetchRowResolvedStatus` call for any slug+location refetches instead of
 * reusing a stale resolved value. Call after an action that can change an
 * integration's status server-side (e.g. `integrations:refetch`, adding an
 * integration).
 */
export function invalidateIntegrationStatuses(): void {
  for (const key of Object.keys(statusPromises)) delete statusPromises[key]
}
